// Standalone preview/test harness for the Matterport lead gate.
//
// Route: /leadgate-preview  (authenticated — just navigate while logged in).
// Runs the gate in DEMO mode (no backend, no Matterport) so the full UX is
// clickable: teaser → form → name → OTP (use 123456) → shake on wrong code →
// resend cooldown → "earned it" success → 30-day unlock. Controls let you
// simulate sweeps and reset the unlock to replay.

import React, { useState } from "react";
import MatterportLeadGate from "rc-app/components/MatterportLeadGate";

const clearLeadGateStorage = () => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith("leadgate:")) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
};

const btn: React.CSSProperties = {
  border: "1px solid rgba(255,255,255,.2)",
  background: "rgba(255,255,255,.06)",
  color: "#e7e9f2",
  borderRadius: 10,
  padding: "9px 14px",
  fontSize: 13,
  cursor: "pointer",
};

const LeadGatePreview: React.FC = () => {
  const [sweepCount, setSweepCount] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [unlockedMsg, setUnlockedMsg] = useState("");

  const reset = () => {
    clearLeadGateStorage();
    setSweepCount(0);
    setUnlockedMsg("");
    setResetKey((k) => k + 1);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        background: "radial-gradient(130% 90% at 50% -10%, #34507e, #0c1424 68%)",
        color: "#e7e9f2",
        fontFamily: "system-ui, -apple-system, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* faux "tour" texture so the gate's frosted blur has something behind it */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.25,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.08) 1px,transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div style={{ position: "relative", maxWidth: 560, margin: "0 auto", padding: "44px 20px 80px" }}>
        <h1 style={{ fontSize: 24, marginBottom: 4 }}>Matterport Lead Gate — Preview</h1>
        <p style={{ color: "#aeb6c6", marginTop: 0 }}>
          Demo mode — no backend. When it asks for an OTP, enter <b style={{ color: "#fff" }}>123456</b>.
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
            margin: "18px 0",
            padding: 14,
            borderRadius: 12,
            background: "rgba(255,255,255,.04)",
            border: "1px solid rgba(255,255,255,.1)",
          }}
        >
          <span style={{ fontSize: 13 }}>
            Sweeps: <b style={{ color: "#E8B96B" }}>{sweepCount}</b> / target 3
          </span>
          <button style={btn} onClick={() => setSweepCount((n) => n + 1)}>
            Simulate a move (sweep +1)
          </button>
          <button style={btn} onClick={() => setSweepCount(3)}>
            Jump to 3 sweeps
          </button>
          <button
            style={{ ...btn, borderColor: "rgba(232,185,107,.5)", color: "#E8B96B" }}
            onClick={reset}
          >
            ↺ Reset (clear unlock + replay)
          </button>
        </div>

        {unlockedMsg && (
          <div
            style={{
              fontSize: 13,
              color: "#34D399",
              background: "rgba(52,211,153,.1)",
              border: "1px solid rgba(52,211,153,.3)",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 16,
            }}
          >
            {unlockedMsg}
          </div>
        )}

        <ol style={{ color: "#aeb6c6", fontSize: 13, lineHeight: 1.7, paddingLeft: 18 }}>
          <li>It arms on load. Hit <b>sweep +1</b> once → a warm teaser pill appears (the pre-cue).</li>
          <li>At <b>3 sweeps</b> (after a ~1.5s dwell) the gate opens — or wait ~8s and it opens anyway (time fallback).</li>
          <li>Enter any 10-digit number → a name → OTP. Use <b>123456</b> to unlock.</li>
          <li>Try a wrong code (e.g. <b>000000</b>) first to see the <b>shake + red border</b>. Resend has a 30s cooldown.</li>
          <li>After unlocking it won't reappear (30-day unlock). Hit <b>Reset</b> to replay.</li>
        </ol>
      </div>

      <MatterportLeadGate
        key={resetKey}
        enabled
        agencyId="demo-agency"
        projectKey="preview-ruchira"
        projectName="Ruchira Villa Feliz"
        sharerName="Sivaram Pandravada"
        agencyName="Ultimeta Advisory"
        sessionId="preview-session"
        sweepCount={sweepCount}
        totalSpaces={10}
        nodeTarget={3}
        minDwellMs={1500}
        maxWaitMs={8000}
        onUnlock={(info) => setUnlockedMsg(`✅ Unlocked — customerId: ${info.customerId}`)}
      />
    </div>
  );
};

export default LeadGatePreview;
